import React, { useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { SingleVerticalBarProps } from './BarChart.types'
import {
  BAR_CHART_DEFAULT_HEIGHT,
  BAR_CHART_DEFAULT_TITLE,
  BAR_CHART_DEFAULT_WIDTH,
  BAR_CHART_SINGLE_COLOR,
} from './BarChart.types'
import { ChartTooltip } from '../ChartTooltip'
import { getBarTooltipAnchor } from './getBarTooltipAnchor'
import './BarChart.css'

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0))

const SingleVerticalBar: React.FC<SingleVerticalBarProps> = ({
  title = BAR_CHART_DEFAULT_TITLE,
  hideTitle = false,
  width = BAR_CHART_DEFAULT_WIDTH,
  height = BAR_CHART_DEFAULT_HEIGHT,
  color = BAR_CHART_SINGLE_COLOR,
  value = 100,
  showTooltip = true,
  formatValue,
  tooltipLabel = 'Value',
  darkMode = false,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const fillRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null)
  const fillPercent = clampPercent(value)
  const format = formatValue ?? ((v: number) => String(v))

  const classNames = [
    'ds-bar-chart',
    'ds-bar-chart--single',
    showTooltip ? 'ds-bar-chart--interactive' : '',
    darkMode ? 'ds-bar-chart--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    '--ds-bar-chart-width': `${width}px`,
    '--ds-bar-chart-height': `${height}px`,
    '--ds-bar-chart-color': color,
    '--ds-bar-chart-fill': `${fillPercent}%`,
  } as CSSProperties

  return (
    <div
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={ariaLabel ?? title}
      onMouseEnter={
        showTooltip
          ? (event) => {
              setHover(
                getBarTooltipAnchor(event.currentTarget, fillRef.current),
              )
            }
          : undefined
      }
      onMouseLeave={showTooltip ? () => setHover(null) : undefined}
    >
      <div className="ds-bar-chart__column" aria-hidden="true">
        <div
          ref={fillRef}
          className={
            fillPercent >= 100
              ? 'ds-bar-chart__fill'
              : 'ds-bar-chart__fill ds-bar-chart__fill--valued'
          }
        />
      </div>
      {!hideTitle ? <p className="ds-bar-chart__title">{title}</p> : null}
      {showTooltip && hover ? (
        <ChartTooltip
          visible
          darkMode={darkMode}
          title={title}
          x={hover.x}
          y={hover.y}
          items={[
            {
              label: tooltipLabel,
              value: format(fillPercent),
              color,
            },
          ]}
        />
      ) : null}
    </div>
  )
}

export default SingleVerticalBar
